/**
 * Timer state as stored on the node. All clients derive the countdown from
 * the same `startedAt`, so every transition is a pure function of
 * (data, now) and writes whole-state patches. Time's up is derived
 * (nothing left), never written: a write at zero could land on top of
 * another client's newer +1 min.
 *
 *   totalSeconds    target of the current run (+1 min extends it)
 *   durationSeconds the chosen duration Reset returns to; older nodes lack it
 *   isRunning       counting down; stays true past zero
 *   startedAt       epoch ms of the last start or resume, null otherwise
 *   elapsedAtPause  seconds elapsed before `startedAt`
 */
export interface TimerData {
  totalSeconds: number;
  durationSeconds: number;
  isRunning: boolean;
  startedAt: number | null;
  elapsedAtPause: number;
}

export type TimerPhase = "ready" | "running" | "urgent" | "paused" | "done";

export interface TimerView {
  phase: TimerPhase;
  /** Whole seconds shown, rounded up so zero lands exactly at time's up. */
  seconds: number;
  digits: string;
  /** Share of the run left, 0 to 1. */
  progress: number;
  /** Milliseconds until the shown second changes; null when not counting. */
  nextTickMs: number | null;
}

export const DEFAULT_SECONDS = 25 * 60;
export const MAX_SECONDS = 99 * 60 + 59;
export const DURATION_CHIPS = [5, 10, 15, 25, 45];

export const INITIAL_TIMER: TimerData = {
  totalSeconds: DEFAULT_SECONDS,
  durationSeconds: DEFAULT_SECONDS,
  isRunning: false,
  startedAt: null,
  elapsedAtPause: 0,
};

function positive(value: unknown): number | null {
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clampSeconds(seconds: number): number {
  return Math.min(MAX_SECONDS, Math.max(1, Math.round(seconds)));
}

export function parseTimer(data: Record<string, unknown>): TimerData {
  const totalSeconds = clampSeconds(
    positive(data.totalSeconds) ?? DEFAULT_SECONDS,
  );
  const isRunning = data.isRunning === true;
  const startedAt =
    typeof data.startedAt === "number" && Number.isFinite(data.startedAt)
      ? data.startedAt
      : null;
  return {
    totalSeconds,
    durationSeconds: clampSeconds(
      positive(data.durationSeconds) ?? totalSeconds,
    ),
    isRunning: isRunning && startedAt !== null,
    startedAt: isRunning ? startedAt : null,
    elapsedAtPause: positive(data.elapsedAtPause) ?? 0,
  };
}

function elapsedMs(d: TimerData, now: number): number {
  const running =
    d.isRunning && d.startedAt !== null ? Math.max(0, now - d.startedAt) : 0;
  return d.elapsedAtPause * 1000 + running;
}

function remainingMs(d: TimerData, now: number): number {
  return Math.max(0, d.totalSeconds * 1000 - elapsedMs(d, now));
}

export function isDone(d: TimerData, now: number): boolean {
  return remainingMs(d, now) === 0;
}

export function formatDigits(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function deriveView(d: TimerData, now: number): TimerView {
  const left = remainingMs(d, now);
  const seconds = Math.ceil(left / 1000);
  const progress = Math.min(1, left / (d.totalSeconds * 1000));
  let phase: TimerPhase;
  if (left === 0) phase = "done";
  else if (d.isRunning) phase = progress <= 0.1 ? "urgent" : "running";
  else phase = d.elapsedAtPause > 0 ? "paused" : "ready";
  return {
    phase,
    seconds,
    digits: formatDigits(seconds),
    progress,
    nextTickMs: d.isRunning && left > 0 ? left - (seconds - 1) * 1000 : null,
  };
}

/** Start, resume, or restart after time's up, which runs the chosen duration. */
export function start(d: TimerData, now: number): TimerData {
  if (isDone(d, now)) {
    return {
      ...d,
      totalSeconds: d.durationSeconds,
      isRunning: true,
      startedAt: now,
      elapsedAtPause: 0,
    };
  }
  return { ...d, isRunning: true, startedAt: now };
}

/** No-op once time is up. */
export function pause(d: TimerData, now: number): TimerData {
  if (!d.isRunning || isDone(d, now)) return d;
  return {
    ...d,
    isRunning: false,
    startedAt: null,
    elapsedAtPause: Math.min(d.totalSeconds, elapsedMs(d, now) / 1000),
  };
}

export function reset(d: TimerData): TimerData {
  return {
    ...d,
    totalSeconds: d.durationSeconds,
    isRunning: false,
    startedAt: null,
    elapsedAtPause: 0,
  };
}

/** +1 min extends the run; after time's up it restarts at 1:00 from now. */
export function addMinute(d: TimerData, now: number): TimerData {
  if (isDone(d, now)) {
    return {
      ...d,
      totalSeconds: 60,
      isRunning: true,
      startedAt: now,
      elapsedAtPause: 0,
    };
  }
  return { ...d, totalSeconds: Math.min(MAX_SECONDS, d.totalSeconds + 60) };
}

export function setDuration(d: TimerData, seconds: number): TimerData {
  const s = clampSeconds(seconds);
  return {
    ...d,
    totalSeconds: s,
    durationSeconds: s,
    isRunning: false,
    startedAt: null,
    elapsedAtPause: 0,
  };
}

/** "12" is minutes, "1:30" is minutes and seconds. */
export function parseDuration(text: string): number | null {
  const match = /^\s*(\d{1,2})(?:\s*:\s*(\d{1,2}))?\s*$/.exec(text);
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = match[2] === undefined ? 0 : Number(match[2]);
  if (seconds > 59) return null;
  const total = minutes * 60 + seconds;
  return total > 0 ? total : null;
}
